<template>
  <div>
    <page-header
      has-return
      :page="pageConfig"
      left-side-class="col-12 col-lg-8"
      right-side-class="col-12 col-lg-4 d-flex align-items-center justify-content-end"
      @onReturn="$router.back"
    >
      <template v-slot:customActions>
        <drop-down
          v-if="hasVisiblePageActions"
          :items="pageActions"
          class="outline-success m-2"
          normal-size
          always-show-dropdown
        />
      </template>
    </page-header>

    <div class="mt-3">
      <calendar
        ref="calendarRef"
        :categories="services"
        @select-appointment="openAppointmentReadOnlyPanel"
      />
    </div>

    <coordinate-appointment-panel
      v-model="showCoordinateAppointmentPanel"
      :appointment-id="activeAppointmentId"
      :is-recoordination="isRecoordinationMode"
      @saved="handleAppointmentSaved"
      @open-signature-hub="openSignatureHub"
    />

    <appointment-read-only-panel
      v-model="showAppointmentReadOnlyPanel"
      :appointment-id="selectedAppointmentId"
      :selected-appointment-purposes="selectedAppointmentPurposes"
      @open-signature-hub="openSignatureHub"
      @recoordinate="handleRecoordinateAppointment"
      @register-celebration="handleRegisterCelebration"
      @confirm-appointment="handleConfirmAppointment"
      @coordinate-appointment="handleCoordinateAppointment"
    />

    <appointment-confirmation-panel
      v-model="showConfirmationPanel"
      :appointment="confirmationAppointment"
      @saved="handleConfirmationSaved"
    />

    <appointment-celebration-panel
      v-model="showCelebrationPanel"
      :appointment="celebrationAppointment"
      @saved="handleCelebrationSaved"
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import DropDown from 'dropdown';
import Calendar from './components/Calendar.vue';
import CoordinateAppointmentPanel from './components/CoordinateAppointmentPanel.vue';
import AppointmentReadOnlyPanel from './components/AppointmentReadOnlyPanel.vue';
import AppointmentConfirmationPanel from './components/AppointmentConfirmationPanel.vue';
import AppointmentCelebrationPanel from './components/AppointmentCelebrationPanel.vue';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';

import studentImpedimentService from '@/modules/student-compu-history/api';

const COORDINATE_APPOINTMENT_PERMISSION = 'pei.compu-coordination.appointment';
const COMPU_APPOINTMENT_CALENDAR_STATE_KEY = 'compuAppointment.calendarState';

export default {
  components: {
    PageHeader,
    ActionButton,
    DropDown,
    Calendar,
    CoordinateAppointmentPanel,
    AppointmentReadOnlyPanel,
    AppointmentConfirmationPanel,
    AppointmentCelebrationPanel,
  },
  data() {
    return {
      services: [],
      showCoordinateAppointmentPanel: false,
      activeAppointmentId: null,
      isRecoordinationMode: false,
      showAppointmentReadOnlyPanel: false,
      selectedAppointmentId: null,
      selectedAppointmentPurposes: [],
      showConfirmationPanel: false,
      confirmationAppointment: null,
      showCelebrationPanel: false,
      celebrationAppointment: null,
    };
  },
  watch: {
    '$route.query.appointmentId': {
      immediate: true,
      handler(id) {
        if (id) {
          this.activeAppointmentId = Number(id);
          this.showCoordinateAppointmentPanel = true;
        }
      },
    },
    showCoordinateAppointmentPanel(isOpen) {
      if (!isOpen) {
        this.activeAppointmentId = null;
        this.isRecoordinationMode = false;
        if (this.$route.query.appointmentId) {
          const { appointmentId, ...rest } = this.$route.query;
          this.$router.replace({ ...this.$route, query: rest });
        }
      }
    },
    '$route.query.readonlyAppointmentId': {
      immediate: true,
      handler(id) {
        if (id) {
          this.selectedAppointmentId = Number(id);
          this.showAppointmentReadOnlyPanel = true;
        }
      },
    },
    showAppointmentReadOnlyPanel(isOpen) {
      if (!isOpen) {
        this.selectedAppointmentId = null;
        this.selectedAppointmentPurposes = [];
        if (this.$route.query.readonlyAppointmentId) {
          const { readonlyAppointmentId, ...rest } = this.$route.query;
          this.$router.replace({ ...this.$route, query: rest });
        }
      }
    },
    showCelebrationPanel(isOpen) {
      if (!isOpen) {
        this.celebrationAppointment = null;
      }
    },
    showConfirmationPanel(isOpen) {
      if (!isOpen) {
        this.confirmationAppointment = null;
      }
    },
  },
  computed: {
    pageConfig() {
      return {
        icon: 'fas fa-calendar-alt',
        title: 'Invitación Reunión COMPU',
        routes: ['Inicio', 'PEI', 'Invitación Reunión COMPU'],
      };
    },
    pageActions() {
      return [
        {
          name: 'Coordinar Cita',
          icon: 'fas fa-calendar-week text-green',
          click: () => {
            this.showCoordinateAppointmentPanel = true;
          },
          visible: this.canCoordinateAppointment,
        },
      ];
    },
    canCoordinateAppointment() {
      return this.userHasPermissions(COORDINATE_APPOINTMENT_PERMISSION);
    },
    hasVisiblePageActions() {
      return this.pageActions.some((action) => action.visible !== false);
    },
  },
  async mounted() {
    await this.getServices();
    this.restoreCalendarState();
  },
  methods: {
    persistCalendarState() {
      const state = this.$refs.calendarRef?.getCalendarState?.();

      if (!state) {
        return;
      }

      sessionStorage.setItem(
        COMPU_APPOINTMENT_CALENDAR_STATE_KEY,
        JSON.stringify(state)
      );
    },
    restoreCalendarState() {
      const rawState = sessionStorage.getItem(
        COMPU_APPOINTMENT_CALENDAR_STATE_KEY
      );

      if (!rawState) {
        return;
      }

      try {
        const state = JSON.parse(rawState);
        this.$nextTick(() => {
          this.$refs.calendarRef?.restoreCalendarState?.(state);
        });
      } catch {
        sessionStorage.removeItem(COMPU_APPOINTMENT_CALENDAR_STATE_KEY);
      }
    },
    async openSignatureHub(payload) {
      const appointmentId =
        typeof payload === 'object' ? payload.appointmentId : payload;
      const keepAppointmentQuery =
        typeof payload === 'object' && payload.keepAppointmentQuery;
      const keepReadonlyQuery =
        typeof payload === 'object' && payload.keepReadonlyQuery;

      if (!appointmentId) {
        return;
      }

      this.persistCalendarState();

      if (keepAppointmentQuery) {
        await this.$router
          .replace({
            name: this.$route.name,
            params: this.$route.params,
            query: {
              ...this.$route.query,
              appointmentId,
            },
          })
          .catch((e) => {
            if (e?.name !== 'NavigationDuplicated') throw e;
          });
      }

      if (keepReadonlyQuery) {
        await this.$router
          .replace({
            name: this.$route.name,
            params: this.$route.params,
            query: { ...this.$route.query, readonlyAppointmentId: appointmentId },
          })
          .catch((e) => {
            if (e?.name !== 'NavigationDuplicated') throw e;
          });
      }

      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: SignatureHubTypes.COMPU_APPOINTMENT,
          relatedId: appointmentId,
          from: 'compuAppointment',
        },
      });
    },
    openAppointmentReadOnlyPanel(appointment) {
      if (!appointment?.id) {
        return;
      }

      this.selectedAppointmentId = Number(appointment.id);
      this.selectedAppointmentPurposes =
        appointment.purposes || appointment.Purposes || [];
      this.showAppointmentReadOnlyPanel = true;
    },
    handleAppointmentSaved() {
      this.$refs.calendarRef?.fetchCalendar?.();
    },
    handleCoordinateAppointment(appointment) {
      if (!appointment?.id) {
        return;
      }

      this.showAppointmentReadOnlyPanel = false;
      this.activeAppointmentId = Number(appointment.id);
      this.isRecoordinationMode = false;
      this.showCoordinateAppointmentPanel = true;
    },
    handleRecoordinateAppointment(appointment) {
      if (!appointment?.id) {
        return;
      }

      this.selectedAppointmentId = null;
      this.selectedAppointmentPurposes = [];
      this.showAppointmentReadOnlyPanel = false;
      this.activeAppointmentId = Number(appointment.id);
      this.isRecoordinationMode = true;
      this.showCoordinateAppointmentPanel = true;
    },
    handleRegisterCelebration(appointment) {
      if (!appointment?.id) {
        return;
      }

      this.celebrationAppointment = appointment;
      this.showCelebrationPanel = true;
    },
    handleConfirmAppointment(appointment) {
      if (!appointment?.id) {
        return;
      }

      this.confirmationAppointment = appointment;
      this.showConfirmationPanel = true;
    },
    handleCelebrationSaved() {
      this.showCelebrationPanel = false;
      this.showAppointmentReadOnlyPanel = false;
      this.$refs.calendarRef?.fetchCalendar?.();
    },
    handleConfirmationSaved() {
      this.showConfirmationPanel = false;
      this.showAppointmentReadOnlyPanel = false;
      this.$refs.calendarRef?.fetchCalendar?.();
    },
    async getServices() {
      const data = await studentImpedimentService.fetchCompuMeetingTypes();

      this.services = (data ?? [])
        .map((service) => ({
          ...service,
          selected: false,
        }))
        .sort((a, b) => a.id - b.id);
    },
  },
};
</script>

<style scoped></style>
